// components/properties/PropertyMap.js
import React, { useEffect, useMemo } from "react";
import { Box, Typography, Button } from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import CurrencyRupee from "@mui/icons-material/CurrencyRupee";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import MapPopup from "./MapPopup";

// --- HELPERS ---

// 1. Safely extract coordinates from various schema shapes
const getCoordinates = (property) => {
  const lat = property.lat || property.geo?.coordinates?.[1]; // GeoJSON [lng, lat]

  const lng = property.lng || property.geo?.coordinates?.[0];

  // Return valid array or null
  if (lat && lng) return [parseFloat(lat), parseFloat(lng)];
  return null;
};

// 2. Safely extract price
const getPrice = (property) => {
  // If property has a direct price
  if (property.price) return property.price;
  // If property has units, get the lowest price
  if (property.units && property.units.length > 0) {
    const prices = property.units.map((u) => u.price).filter((p) => p);
    return Math.min(...prices);
  }
  return null;
};

const formatPrice = (price) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Number(price || 0)
  );

// 3. Create Custom Price Pin
export const createPriceIcon = (price) => {
  const inner = renderToStaticMarkup(
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        fontWeight: 700,
      }}
    >
      <CurrencyRupee
        style={{ fontSize: 15, transform: "translateY(-0.75px)" }}
      />
      <span style={{ fontSize: 14 }}>{formatPrice(price)}</span>
    </span>
  );

  return L.divIcon({
    className: "custom-price-marker",
    html: `
      <div style="
        background-color: #fff;
        color: #111;
        padding: 4px 8px;
        border-radius: 12px;
        border: 1px solid rgba(0,0,0,0.08);
        box-shadow: 0 2px 6px rgba(0,0,0,0.12);
        font-weight: 700;
        font-size: 12px;
        white-space: nowrap;
        display: inline-block;
        transform: translate(-50%, -50%);
      ">
        ${inner}
      </div>
    `,
    iconSize: [40, 20],
    iconAnchor: [20, 10],
  });
};

// --- SUB-COMPONENT FOR AUTO-ZOOM ---
// This handles the "Fly To" and "Fit Bounds" logic
const MapController = ({ markers }) => {
  const map = useMap();

  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map((m) => m.coords));
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 16, // Don't zoom in too close if only 1 pin
      });
    }
  }, [markers, map]);

  return null;
};

// --- MAIN COMPONENT ---
const PropertyMap = ({ properties = [] }) => {
  // 1. Process Data: Filter out invalid properties immediately
  const validProperties = useMemo(() => {
    return properties
      .map((p) => ({
        ...p,
        coords: getCoordinates(p),
        displayPrice: getPrice(p),
      }))
      .filter((p) => p.coords !== null); // STRICT FILTER: Drop items with no coords
  }, [properties]);

  // Default center (Dubai) - only used if NO properties exist
  //   const defaultCenter = [25.2048, 55.2708];
  const PATNA_CENTER = [25.5941, 85.1376];

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <MapContainer
        center={PATNA_CENTER}
        zoom={12}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Controller to handle Auto-Zoom */}
        <MapController markers={validProperties} />

        {/* Render Markers */}
        {validProperties.map((property) => (
          <Marker
            key={property._id || property.id}
            position={property.coords}
            icon={createPriceIcon(property.displayPrice)}
            eventHandlers={{
              //   mouseover: (e) => e.target.openPopup(),
              onclick: (e) => e.target.openPopup(),
              // mouseout: (e) => e.target.closePopup(),
            }}
          >
            <Popup>
              <MapPopup property={property} />
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* "Show All" Button (Optional overlay) */}
      {/* You can re-enable this if needed, but MapController handles it automatically on load */}
    </Box>
  );
};

export default PropertyMap;
